import deepEqual from 'fast-deep-equal'
import type { CalculatedSlot } from '@shared/schemas'

export interface ExtraData {
  extraLocation?: string
  extraCompanyName?: string
  extraCompanyAddress?: string
  extraCompanyId?: string
  extraNotes?: string
}

export interface SlotGroup {
  startTime: string
  endTime: string
  type: 'companyVisit' | 'siteInspection' | 'extra' | 'region'
  data:
    | CalculatedSlot['companyVisit']
    | CalculatedSlot['siteInspection']
    | ExtraData
    | CalculatedSlot['region']
}

export function groupSpecialSlots(
  slots: Array<CalculatedSlot>,
): Array<SlotGroup> {
  const groups: Array<SlotGroup> = []
  const activeGroups = new Map<SlotGroup['type'], SlotGroup>()
  const orderedSlots = [...slots].sort((a, b) => a.time.localeCompare(b.time))

  for (const slot of orderedSlots) {
    const notices: Array<Pick<SlotGroup, 'type' | 'data'>> = []
    if (slot.companyVisit) {
      notices.push({ type: 'companyVisit', data: slot.companyVisit })
    }
    if (slot.siteInspection) {
      notices.push({ type: 'siteInspection', data: slot.siteInspection })
    }
    // A site's location belongs to its inspection card, not a generic extra card.
    if (
      slot.extraReason !== 'site_inspection' &&
      (slot.extraLocation || slot.extraCompanyId)
    ) {
      notices.push({
        type: 'extra',
        data: {
          extraLocation: slot.extraLocation,
          extraCompanyName: slot.extraCompanyName,
          extraCompanyAddress: slot.extraCompanyAddress,
          extraCompanyId: slot.extraCompanyId,
          extraNotes: slot.extraNotes,
        },
      })
    }
    if (slot.region) {
      notices.push({ type: 'region', data: slot.region })
    }

    for (const type of activeGroups.keys()) {
      if (!notices.some((notice) => notice.type === type)) {
        activeGroups.delete(type)
      }
    }
    for (const notice of notices) {
      const current = activeGroups.get(notice.type)
      if (
        current &&
        current.endTime === slot.time &&
        deepEqual(current.data, notice.data)
      ) {
        current.endTime = slot.endTime
      } else {
        const group = {
          ...notice,
          startTime: slot.time,
          endTime: slot.endTime,
        }
        groups.push(group)
        activeGroups.set(notice.type, group)
      }
    }
  }

  return groups
}
